import { V4ClassEnum } from 'types/v4ClassEnum';
import { format } from 'date-fns';
import { toImageAnswerRequestValue } from 'utils/fieldNotebookUtils/imageAnswer';
import { HistoricAnswer } from '../historics/types';
import { Organization } from '../organization/types';
import { Answer, AnswerSubfield, Field, Form, Item } from './types';

/**
 * Converte o valor guardado no estado para o formato enviado à API:
 * campos de imagem viram array de itens (signed_id / URL), checkbox vira array
 * de opções; os demais seguem como estão.
 */
export function toRequestAnswerValue(
  value: string | null | AnswerSubfield[],
  componentType?: string,
): string | string[] | null | AnswerSubfield[] {
  if (componentType === 'image') return toImageAnswerRequestValue(value);

  if (typeof value === 'string' && value.includes('\t')) {
    return value.split('\t').slice(1);
  }

  return value;
}

export function generateUpdateAnswersData(
  answers: Answer[] | AnswerSubfield[],
  historicAnswers: HistoricAnswer[],
) {
  const answersData: {
    answer_id: number;
    value: string | string[] | null | AnswerSubfield[];
  }[] = [];

  answers.forEach((answer) => {
    const fieldId = (answer as Answer).field_id;
    const historicAnswer = historicAnswers.find((item) => item.field_id === fieldId);
    // Sem resposta histórica correspondente não há answer_id para atualizar.
    if (!historicAnswer) return;

    const newAnswer = {
      answer_id: historicAnswer.answer_id,
      value: toRequestAnswerValue(answer.value, historicAnswer.component_type),
    };
    answersData.push(newAnswer);
  });

  return {
    answers: answersData,
  };
}

function firsThreeLetters(text: string): string {
  if (text.length < 3) return text.toUpperCase();
  return text.slice(0, 3).toUpperCase();
}

export function gerenateAutoFieldValue({
  externalId,
  firstName,
  lastName,
}: {
  externalId: string;
  firstName: string;
  lastName: string;
}) {
  const now = new Date();
  const formattedDate = format(now, 'dd/MM/yy-HH:mm:ss');
  const threeLettersFirstName = firsThreeLetters(firstName);
  const threeLettersLastName = firsThreeLetters(lastName);
  return `${externalId}-${threeLettersFirstName}${threeLettersLastName}-${formattedDate}`;
}

export function formatForm(
  form: Form,
  organization: Organization,
  externalId: string,
): Form {
  const newFields = form.fields.map((field) => ({
    ...field,
    items: field.v4_class
      ? organization[V4ClassEnum[field.v4_class]].map(
          (v4Item) =>
            ({
              id: v4Item.id.toString(),
              name: v4Item.name,
              label: v4Item.name,
              value: v4Item.name,
            }) as Item,
        )
      : field.items,
  }));

  newFields.forEach((field) => {
    field.items = field.items?.filter((item) => {
      if (item.company_id) {
        return item.company_id?.toString() === externalId?.toString();
      }
      return true;
    });
  });

  return {
    ...form,
    fields: newFields,
  };
}

export function verifyNeedsCleanConditionalAnswersWhenEditing(
  answer: Answer,
  answersList: Answer[],
  fields: Field[],
): boolean {
  const existingAnswer = answersList.find(
    (ans: { field_id: any }) => ans.field_id === answer.field_id,
  );

  if (existingAnswer) {
    const answerField = fields.find(
      (field) => field.field_id === answer.field_id,
    );
    const isConditionalResponse = answerField?.component_depends?.length !== 0;

    if (!isConditionalResponse || existingAnswer.value === null) {
      return false;
    }

    return existingAnswer.value !== answer.value;
  }

  return false;
}

export function clearAnswersToNull(
  answers: Answer[],
  startIndex: number,
): Answer[] {
  const clearAnswers = answers.map((answer, index) => {
    if (index >= startIndex) {
      return {
        field_id: answer.field_id,
        value: null,
      };
    }
    return answer;
  });

  return clearAnswers;
}
